import React, { useEffect, useState } from 'react'
import './AssignPlan.scss'
import { Button } from './Button.jsx'
import { Table } from './Table'
import { Modal } from './Modal.jsx'
import { FormToggle } from './FormToggle'
import { formatToDate } from '../../util/formatDate'
import { useModuleCopy } from 'context'

import { ReactComponent as UsersSVG } from '../stories/assets/users.svg'
import { useMemo } from 'react'

const stages = {
  ASSIGN: 'assign',
  DEADLINES: 'deadlines'
}

/**
 * @param {Object} props
 * @param {Object} props.controls
 * @param {(pagination: Object) => Promise<{ data: [], count: number }>} props.list
 * @param {{ [key: number]: boolean }} [props.disabledRows={}]
 * @param {Object} [props.details={}]
 * @param {(value: string) => void} [props.onSearch]
 * @param {(selectedPlans: Array<{ id: number, deadlineDate: string }>) => void} [props.onAssign]
 */
export default function AssignPlan({ controls, list = [], disabledRows = {}, details = {}, onSearch = (value) => {}, onAssign = (selectedPlans) => {} }) {
  /**
   * State storing which stage of the assign wizard we're on.
   *
   * 'assign' is step one.
   * 'deadlines' is step two.
   */
  const [stage, setStage] = useState(stages.ASSIGN)
  const [selectedPlans, setSelectedPlans] = useState({})
  const {
    textCopy: {
      audits: {
        assign: { title }
      }
    }
  } = useModuleCopy()

  // Set stage to start back when modal closes.
  useEffect(() => {
    if (!controls.isOpen) {
      setStage(stages.ASSIGN)
      setSelectedPlans({})
    }
  }, [controls.isOpen])

  /**
   * Buttons to show on the modal, update dynamically via stage.
   */
  const buttons = useMemo(
    () => (
      <>
        {stage === stages.ASSIGN && (
          <Button type='primary' disabled={!Object.keys(selectedPlans || {}).length} onClick={() => setStage(stages.DEADLINES)}>
            Next
          </Button>
        )}
        {stage === stages.DEADLINES && (
          <>
            <Button type='primary' onClick={() => setStage(stages.ASSIGN)}>
              Back
            </Button>
            <Button
              type='primary'
              onClick={() =>
                onAssign(
                  Object.values(selectedPlans).map(({ id, deadlineDate }) => ({
                    id,
                    deadlineDate
                  }))
                )
              }
            >
              Assign
            </Button>
          </>
        )}
      </>
    ),
    [stage, setStage, selectedPlans, onAssign]
  )

  /**
   * Shows a selected plan and allows deadline date input.
   */
  const SelectedPlanTile = ({ onChange = () => {}, pepId }) => {
    // Has deadline state.
    const [hasDeadline, setHasDeadline] = useState(false)

    // Emit undefined to clear date when switched to no.
    useEffect(() => {
      if (!hasDeadline) onChange(undefined)
    }, [hasDeadline, onChange])

    return (
      <div className='deadline-title'>
        <div className='title-half left'>
          <p>
            Plan <b>{pepId}</b>
          </p>
          <FormToggle
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]}
            default={false}
            onChange={(val) => {
              setHasDeadline(val)
            }}
          ></FormToggle>
        </div>
        <div className='title-half centre'>
          {hasDeadline ? (
            <>
              <input
                type='date'
                placeholder='Deadline Date'
                onChange={(e) => {
                  onChange(e.target.value)
                }}
              ></input>
            </>
          ) : (
            'No Deadline'
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal buttons={buttons} title={title} controls={controls} closeText='Cancel'>
      <div className='assign-plans'>
        <div className='auditor'>
          <span>NAME:</span>
          <input className='audit-field' readOnly value={details?.name} />
          <span>EMAIL:</span>
          <input className='audit-field' readOnly value={details?.email} />
        </div>
        {stage === stages.ASSIGN ? (
          <>
            <div className='search-wrapper'>
              <input placeholder='Search' onChange={({ target: { value } }) => onSearch(value)} />
            </div>
            <Table
              defaultSortInfo={{ name: 'createdAt', dir: -1 }}
              columns={[
                { label: 'Internal ID', dataKey: 'pepId', width: 100 },
                {
                  label: 'Current Care',
                  dataKey: 'currentSocialCare',
                  width: 200
                },
                { label: 'Key Stage', dataKey: 'keyStage', width: 200 },
                {
                  label: 'Venue',
                  dataKey: 'settingName',
                  minWidth: 150,
                  flexGrow: 4
                },
                {
                  label: 'Uploaded',
                  dataKey: 'createdAt',
                  width: 100,
                  cellDataGetter: ({ dataKey, rowData }) => {
                    if (typeof rowData.get === 'function') {
                      return rowData.get(dataKey)
                    } else {
                      return rowData[dataKey]
                    }
                  },
                  render: ({ value }) => formatToDate(value)
                },
                { label: 'Team Region', dataKey: 'teamRegion', width: 120 },
                {
                  label: 'Assigned to',
                  dataKey: 'pendingReviewers',
                  minWidth: 80,
                  flexGrow: 2,
                  render: ({ data: { pendingReviewers } }) => {
                    return (
                      <div className='assignees'>
                        <UsersSVG />
                        <span>{pendingReviewers >= 1 ? pendingReviewers : ''}</span>
                      </div>
                    )
                  }
                }
              ]}
              dataSource={list}
              disabledRows={disabledRows}
              checkboxColumn={true}
              multiSelect={true}
              selected={selectedPlans}
              onSelectionChange={(val) => {
                /**
                 * Not in docs (reactdatagrid-community not supported anymore)
                 * but basically when val.selected is "true" that means "select all".
                 * When val.selected is an empty object {} that means "deselect all".
                 * When val.selected is an object, the keys are the ID of the selected rows
                 * and the value is the row itself.
                 */
                if (val.selected === true) {
                  const selected = val.originalData.reduce((acc, d, i) => {
                    if (!disabledRows[i]) {
                      acc[d.id] = d
                    }
                    return acc
                  }, {})
                  setSelectedPlans(selected)
                  return
                }
                setSelectedPlans(val?.selected)
              }}
              idProperty='id'
              emptyText={
                <section>
                  <h2 className='text-center'>No plans found.</h2>
                  <br /> To upload plans, go to <a href='/coordinator/upload-data'>Coordinator &gt; Upload Data</a>.
                </section>
              }
              storageKey='assign-plans'
            />
          </>
        ) : (
          <div className='assign-deadlines'>
            <p>Would you like to set any deadlines?</p>
            <div className='deadline-list'>
              {Object.entries(selectedPlans || {}).map(([key, { pepId, id: planId }]) => (
                <SelectedPlanTile
                  key={key}
                  pepId={pepId}
                  onChange={(date) => {
                    // Update selected plans with deadline.
                    const newPlans = selectedPlans
                    newPlans[key].deadlineDate = date
                    setSelectedPlans(newPlans)
                  }}
                ></SelectedPlanTile>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
